/**
 * MSAL temporary-cache recovery (2026-09-07 incident: "Sesja wygasła" that
 * survives a successful re-login in the SAME tab).
 *
 * MSAL keeps its interaction bookkeeping in a TEMPORARY cache that is ALWAYS
 * sessionStorage — regardless of `cache.cacheLocation` (verified in
 * @azure/msal-browser 5.21: BrowserCacheManager constructs
 * `temporaryCacheStorage` with BrowserCacheLocation.SessionStorage). The key
 * `msal.interaction.status` is written by every interactive entry point
 * (loginRedirect / acquireTokenRedirect call setInteractionInProgress(true,
 * SIGNIN) WITHOUT allowOverride) and cleared when the redirect response comes
 * back through handleRedirectPromise.
 *
 * That flag can be left behind — a redirect that never completed, or (before
 * the main.tsx iframe guard) a second PublicClientApplication booted inside
 * the hidden silent-renewal iframe calling resetRequestCache() /
 * setInteractionInProgress(false) underneath the top window. A stale flag
 * makes every LATER redirect throw `interaction_in_progress`, so the user is
 * stuck in the modal for the life of the tab while a fresh tab (empty
 * sessionStorage) works — exactly the reported symptom.
 *
 * Clearing the flag is safe: the worst case is that a genuinely in-flight
 * interaction is restarted, which is what the user just asked for by clicking
 * "Zaloguj się ponownie".
 */

/** `msal.interaction.status` — PREFIX is a bare "msal", no client id. */
const INTERACTION_STATUS_KEY = 'msal.interaction.status';

/**
 * Per-request temporary keys, written as `msal.<clientId>.<TemporaryCacheKey>`
 * (BrowserCacheManager.generateCacheKey). Names verified against
 * @azure/msal-browser 5.21 `TemporaryCacheKeys`: request.origin, urlHash,
 * request.params, code.verifier, request.native.
 */
const REQUEST_KEY_PATTERN =
  /^msal\..*\.(request\.params|request\.origin|code\.verifier|urlHash|request\.native)$/;

/**
 * Drop MSAL's pending-interaction bookkeeping so the next redirect can start.
 * Never throws — sessionStorage access itself can throw (Safari private mode,
 * blocked storage), and failing to clean up must never break sign-in.
 */
export function clearStaleMsalInteraction(): void {
  try {
    const store = window.sessionStorage;
    const doomed: string[] = [INTERACTION_STATUS_KEY];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key && REQUEST_KEY_PATTERN.test(key)) doomed.push(key);
    }
    doomed.forEach((key) => store.removeItem(key));
    console.info('[Auth] Cleared stale MSAL interaction state:', doomed.length, 'key(s)');
  } catch (error) {
    console.warn('[Auth] Could not clear MSAL interaction state:', error);
  }
}

/**
 * "Interactive fallback already fired" latch (2026-09-07).
 *
 * Guards the automatic acquireTokenRedirect in useAuth so it happens EXACTLY
 * ONCE — never a redirect loop. It lives in sessionStorage because it must
 * survive the redirect round-trip: if we come back and silent acquisition
 * still fails, the SessionExpiredModal takes over instead of bouncing the
 * user to AAD again. It goes stale after 5 minutes so a long-lived tab can
 * recover later.
 *
 * It lives HERE, not in useAuth, because the modal's "Odśwież stronę" button
 * must be able to reset it: sessionStorage survives a same-tab reload, so a
 * reload alone is NOT the "open a fresh tab" equivalent it advertises — the
 * latch would still be armed and the first failing renewal would re-open the
 * modal without ever attempting the redirect.
 */
const INTERACTIVE_RETRY_KEY = 'periscope-msal-interactive-retry';
const INTERACTIVE_RETRY_TTL_MS = 5 * 60 * 1000;

export function interactiveRetryPending(): boolean {
  try {
    const raw = sessionStorage.getItem(INTERACTIVE_RETRY_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts) || Date.now() - ts > INTERACTIVE_RETRY_TTL_MS) {
      sessionStorage.removeItem(INTERACTIVE_RETRY_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function markInteractiveRetry(): void {
  try {
    sessionStorage.setItem(INTERACTIVE_RETRY_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export function clearInteractiveRetry(): void {
  try {
    sessionStorage.removeItem(INTERACTIVE_RETRY_KEY);
  } catch {
    // ignore
  }
}
